import Toast, { ToastAction, ToastType } from '@/components/Toast';
import React, { createContext, useCallback, useContext, useState } from 'react';

interface ToastOptions {
  title?: string;
  description?: string;
  message?: string;
  type?: ToastType;
  duration?: number;
  action?: ToastAction | ToastAction[];
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
  showActionToast: (options: ToastOptions) => void;
  hideToast: () => void;
}

interface ToastState {
  id: number;
  message: string;
  title?: string;
  description?: string;
  type: ToastType;
  duration: number;
  action?: ToastAction | ToastAction[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastState[]>([]);

    const showToast = useCallback((message: string, type: ToastType = 'info') => {
        setToasts(prev => [
            ...prev,
            {
                id: Date.now() + Math.random(),
                message,
                type,
                duration: 3000,
            },
        ]);
    }, []);

    const showActionToast = useCallback((options: ToastOptions) => {
        setToasts(prev => [
            ...prev,
            {
                id: Date.now() + Math.random(),
                message: options.message || '',
                title: options.title,
                description: options.description,
                type: options.type || 'info',
                duration: options.duration ?? 3000,
                action: options.action,
            },
        ]);
    }, []);

    const removeToast = useCallback((id: number) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const hideToast = useCallback(() => {
        setToasts([]);
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, showActionToast, hideToast }}>
            {children}
            <div className="pointer-events-none fixed right-4 top-4 z-[99999] flex flex-col gap-3">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        title={toast.title}
                        description={toast.description}
                        type={toast.type}
                        action={toast.action}
                        onClose={() => removeToast(toast.id)}
                        duration={toast.duration}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}; 
